import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { Book } from '../types/Book.types';
import { MOCK_BOOKS } from '../mocks/Books.mocks';
import { getAccessToken, buildQuery, unwrapList, unwrapRecord, unwrapError, getTransportError } from './apiUtils';

const ROUTES = {
  list:   '/books_list',
  create: '/books_create',
  update: '/books_update'
};

const BOOK_STATUSES: Book['status'][] = ['editorial', 'printing', 'warehouse', 'distributed', 'out-of-stock'];

/**
 * La tabla `books` no guarda stock ni lastUpdated: el stock real se deriva de
 * stock_movements (ver StockMovementsService) y no de esta tabla.
 */
export const mapBook = (raw: any): Book => ({
  id:          String(raw.id),
  title:       raw.title || '',
  author:      raw.author || '',
  isbn:        raw.isbn || '',
  sku:         raw.sku || '',
  publisher:   raw.publisher || '',
  status:      BOOK_STATUSES.indexOf(raw.status) >= 0 ? raw.status : 'editorial',
  stock:       isNaN(Number(raw.stock)) ? 0 : Number(raw.stock),
  price:       isNaN(Number(raw.price)) ? 0 : Number(raw.price),
  category:    raw.category || '',
  lastUpdated: raw.updated_at || raw.created_at || ''
});

/** Mismo criterio que WarehousesService: lectura con red de seguridad, escritura sin ella. */
export const listBooks = async (
  options: { status?: string; search?: string; page?: number; pageSize?: number } = {}
): Promise<Book[]> => {
  const token = getAccessToken();
  if (!token) return MOCK_BOOKS;

  const query = buildQuery({
    access_token: token,
    status:       options.status,
    search:       options.search,
    page:         options.page || 1,
    page_size:    options.pageSize || 200
  });

  try {
    const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${query}`);
    const list = unwrapList(res);
    if (list.length === 0) return MOCK_BOOKS;
    return list.map(mapBook);
  } catch (err) {
    console.warn('books_list no disponible, usando datos de maqueta:', err);
    return MOCK_BOOKS;
  }
};

export const createBook = async (book: Omit<Book, 'id' | 'lastUpdated'>): Promise<Book> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token: token,
    title:        book.title,
    author:       book.author,
    isbn:         book.isbn,
    sku:          book.sku,
    publisher:    book.publisher,
    status:       book.status,
    price:        book.price,
    category:     book.category
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.create}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al registrar la obra.'));
  // Si no se envía sku, books/create.avap genera LIB + id a 6 dígitos.
  return mapBook(record);
};

export const updateBook = async (id: string, book: Partial<Book>): Promise<Book> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token: token,
    id,
    title:        book.title,
    author:       book.author,
    isbn:         book.isbn,
    sku:          book.sku,
    publisher:    book.publisher,
    status:       book.status,
    price:        book.price,
    category:     book.category
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.update}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al actualizar la obra.'));
  return mapBook(record);
};

// En los libros el EAN-13 es el ISBN-13, así que los dos se comparan sin guiones.
const normalizeIsbn = (value: string) => value.replace(/[^0-9Xx]/g, '').toUpperCase();

/** Libro del catálogo por ISBN o EAN (sin guiones) o por SKU, para los lectores de código de barras. */
export const findBookByCode = (books: Book[], code: string, by: 'isbn' | 'sku'): Book | undefined => {
  const value = code.trim();
  if (by === 'sku') return value ? books.find(b => (b.sku || '').toLowerCase() === value.toLowerCase()) : undefined;
  const isbn = normalizeIsbn(value);
  return isbn ? books.find(b => normalizeIsbn(b.isbn || '') === isbn) : undefined;
};

export interface SellableBook {
  id: string;
  title: string;
  isbn: string;
  sku: string;
  price: number;
  /** Existencias de todos los almacenes (SUM de stock_movements en books_list). */
  stock: number;
}

/**
 * Catálogo con existencias reales para facturar. A diferencia de listBooks no
 * cae a datos de maqueta: con stock inventado el control no valdría nada. Un
 * libro archivado cuenta como sin stock, igual que en el TPV.
 */
export const listBooksForSale = async (): Promise<SellableBook[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${buildQuery({ access_token: token, page: 1, page_size: 1000 })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);

  return unwrapList(res).map((raw: any): SellableBook => ({
    id:    String(raw.id),
    title: raw.title || '',
    isbn:  raw.isbn || '',
    sku:   raw.sku || '',
    price: isNaN(Number(raw.price)) ? 0 : Number(raw.price),
    stock: raw.status === 'archived' ? 0 : (isNaN(Number(raw.stock)) ? 0 : Number(raw.stock))
  }));
};
